import React, { useCallback, useContext, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Button, IconButton, Portal, Typography } from "@mui/material";
import { Rnd } from "react-rnd";
import { makeStyles } from "tss-react/mui";

import Dialog from "@/components/Dialog";
import { colors } from "@/theme/overrides/colors";

import useDialogState from "./useDialogState";

const useStyles = makeStyles()(() => ({
  pipResizableContent: {
    zIndex: 99,
    background: "#202429",
    borderRadius: 4,
    color: colors.white,
  },
  closeButton: {
    position: "absolute",
    top: 4,
    right: 4,
  },
  exitDialogPaper: {
    minWidth: 400,
    maxWidth: 400,
  },
  exitDialogContent: {
    paddingTop: 0,
  },
  exitDialogIcon: {
    maxHeight: 60,
    fontSize: 60,
    color: colors.secondary,
  },
  exitDialogTitle: {
    fontWeight: 700,
    fontSize: 28,
    lineHeight: 1,
    color: colors.dark,
    textAlign: "center",
  },
  exitDialogMessage: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "22px",
    color: colors.grey,
    textAlign: "center",
  },
  confirmDialogButton: {
    minWidth: 125,
  },
  cancelDialogButton: {
    minWidth: 125,
  },
  itemIcon: {
    minWidth: 30,
  },
}));

interface Helpers {
  open: (wherebyRoomId: string) => void;
  close: () => void;
}

const GlobalPictureInPictureContext = React.createContext<Helpers | null>(null);
GlobalPictureInPictureContext.displayName = "GlobalPictureInPictureContext";

export const GlobalPictureInPictureProvider: React.FC = ({ children }) => {
  const { classes } = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [wherebyRoomUrl, setWherebyRoomUrl] =
    useState<string | undefined>(undefined);

  const open = useCallback((wherebyRoomUrl: string) => {
    setIsOpen(true);
    setWherebyRoomUrl(wherebyRoomUrl);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const exitDialog = useDialogState();

  const handleExit = () => {
    exitDialog.show();
  };

  const helpers = useMemo<Helpers>(() => ({ open, close }), []);

  return (
    <GlobalPictureInPictureContext.Provider value={helpers}>
      {children}
      {isOpen && wherebyRoomUrl && (
        <Rnd
          onMouseDown={(e) => e.stopPropagation()}
          className={classes.pipResizableContent}
          default={{
            x: 800,
            y: 400,
            width: 765,
            height: 430,
          }}
          minWidth={400}
          minHeight={190}
          maxWidth={765}
          maxHeight={430}
          lockAspectRatio
          bounds="window"
        >
          <Box
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
            padding={6}
          >
            <IconButton
              onClick={handleExit}
              color="secondary"
              className={classes.closeButton}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Box height="100%" width="100%">
              <iframe
                src={wherebyRoomUrl}
                allow="camera; microphone; speaker; fullscreen; display-capture"
                style={{ height: "100%", width: "100%" }}
              />
            </Box>
          </Box>
        </Rnd>
      )}
      <ExitModal
        visible={exitDialog.shown}
        title=""
        message="Are you sure you want to exit meeting?"
        onExit={close}
        onClose={exitDialog.hide}
      />
    </GlobalPictureInPictureContext.Provider>
  );
};

export default function useGlobalPictureInPicture(): Helpers {
  const value = useContext(GlobalPictureInPictureContext);

  if (value == null)
    throw new Error("GlobalPictureInPictureContext.Provider not found");

  return value;
}

type DeleteProps = {
  title: string;
  message: string;
  visible: boolean;
  onClose: () => void;
  onExit: (id?: string) => void | Promise<void>;
};

const ExitModal: React.FC<DeleteProps> = ({
  onExit,
  title,
  message,
  onClose,
  visible,
}) => {
  const { classes } = useStyles();

  const closeExitDialog = () => {
    onClose();
  };

  const handleExit = () => {
    onExit();
    closeExitDialog();
  };

  return (
    <>
      <Portal>
        <Dialog
          title={title}
          open={visible}
          onClose={onClose}
          classes={{ paper: classes.exitDialogPaper }}
          contentClassName={classes.exitDialogContent}
          content={
            <Box display="flex" flexDirection="column" alignItems="center">
              <div className={classes.exitDialogIcon}>
                <WarningIcon fontSize="inherit" color="inherit" />
              </div>
              <Box marginBottom={5} />
              <Typography className={classes.exitDialogTitle}>
                Exit meeting?
              </Typography>
              <Box marginBottom={2} />
              <Typography className={classes.exitDialogMessage}>
                {message}
              </Typography>
            </Box>
          }
          actions={
            <Box width="100%" display="flex" justifyContent="space-evenly">
              <Button
                size="large"
                className={classes.confirmDialogButton}
                onClick={handleExit}
              >
                Yes
              </Button>

              <Button
                size="large"
                className={classes.cancelDialogButton}
                onClick={closeExitDialog}
                variant="outlined"
              >
                No
              </Button>
            </Box>
          }
        />
      </Portal>
    </>
  );
};
